/**
 * The canonical dish-type taxonomy and the FR/EN vocabulary that maps onto it.
 *
 * Single source of truth, shared by every layer that has an opinion about what
 * kind of dish a recipe is:
 *   - import      (api/recipes/import.ts, tooling/importKatrineBatch.ts)
 *   - the planner (src/utils/recipeClassification.ts)
 *   - backfills   (scripts/backfillDishTypes.ts)
 *
 * Lives in src/utils (not api/_lib) on purpose: it has no dependencies, so
 * client code, API handlers and tooling can all import it. The Gemini fallback
 * layer stays behind in api/_lib/classifyDishType.ts, which needs firebase-admin.
 *
 * Why this exists: scraped recipes arrive tagged in the source site's own
 * language and phrasing — ["Déjeuners et brunchs, Recettes"], ["Plat
 * d'accompagnement"], ["Collation"] — and consumers used to test them with
 * English substring matches (`dt.includes('breakfast')`). Every French tag read
 * as "unclassified", which the planner treats as "ordinary meal, fine for
 * souper". That is how a breakfast galette became a Thursday dinner.
 */

export const CANONICAL_DISH_TYPES = [
  'breakfast',
  'main course',
  'dessert',
  'snack',
  'appetizer',
  'side dish',
  'salad',
  'soup',
  'beverage',
  'sauce',
] as const;

export type CanonicalDishType = (typeof CANONICAL_DISH_TYPES)[number];

export interface ClassificationResult {
  dishType: CanonicalDishType;
  confidence: number;
  source: 'normalized' | 'url-path' | 'heuristic' | 'gemini';
  /**
   * How many of the user's own corrections primed the model. Set only on the
   * `gemini` path — the sync layers do not read examples. Reported to PostHog
   * so the effect of corrections on classification is measurable rather than
   * assumed.
   */
  fewShotCount?: number;
}

/** Lowercase + strip accents + straighten apostrophes, so "Plat d’accompagnement" matches. */
export function fold(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2018\u2019\u02bc]/g, "'")
    .trim();
}

// Keys are accent-folded. "déjeuner" maps to breakfast (Quebec French).
export const CATEGORY_MAP: Record<string, CanonicalDishType> = {
  // main course
  'main course': 'main course',
  'main dish': 'main course',
  'main dishes': 'main course',
  'main': 'main course',
  'plat principal': 'main course',
  'plats principaux': 'main course',
  'plat': 'main course',
  'plats': 'main course',
  'dinner': 'main course',
  'souper': 'main course',
  'diner': 'main course',
  'lunch': 'main course',
  'entree (us)': 'main course',
  // main course — French section names used by Ricardo / Trois fois par jour
  'viandes et volailles': 'main course',
  'poissons et fruits de mer': 'main course',
  'plats vegetariens': 'main course',
  'riz': 'main course',
  'pates et pizza': 'main course',
  'riz, pates et pizza': 'main course',
  'sandwichs et burgers': 'main course',
  'sandwich': 'main course',
  'sandwichs': 'main course',
  'burgers': 'main course',
  'lunchs': 'main course',
  // breakfast
  'breakfast': 'breakfast',
  'brunch': 'breakfast',
  'brunchs': 'breakfast',
  'brunches': 'breakfast',
  'petit-dejeuner': 'breakfast',
  'petit dejeuner': 'breakfast',
  'petits-dejeuners': 'breakfast',
  'dejeuner': 'breakfast',
  'dejeuners': 'breakfast',
  'dejeuners et brunchs': 'breakfast',
  // dessert
  'dessert': 'dessert',
  'desserts': 'dessert',
  'patisserie': 'dessert',
  'patisseries': 'dessert',
  'sweets': 'dessert',
  'baking': 'dessert',
  // snack
  'snack': 'snack',
  'snacks': 'snack',
  'collation': 'snack',
  'collations': 'snack',
  'gouter': 'snack',
  // appetizer
  'appetizer': 'appetizer',
  'appetizers': 'appetizer',
  'appetiser': 'appetizer',
  'starter': 'appetizer',
  'starters': 'appetizer',
  'entree': 'appetizer',
  'entrees': 'appetizer',
  'aperitif': 'appetizer',
  'aperitifs': 'appetizer',
  "hors d'oeuvre": 'appetizer',
  'amuse-bouche': 'appetizer',
  'antipasti': 'appetizer',
  // side dish
  'side dish': 'side dish',
  'side dishes': 'side dish',
  'side': 'side dish',
  'sides': 'side dish',
  'accompagnement': 'side dish',
  'accompagnements': 'side dish',
  "plat d'accompagnement": 'side dish',
  "plats d'accompagnement": 'side dish',
  // salad
  'salad': 'salad',
  'salads': 'salad',
  'salade': 'salad',
  'salades': 'salad',
  // soup
  'soup': 'soup',
  'soups': 'soup',
  'soupe': 'soup',
  'soupes': 'soup',
  'potage': 'soup',
  'potages': 'soup',
  'soupes et potages': 'soup',
  // beverage
  'beverage': 'beverage',
  'beverages': 'beverage',
  'drink': 'beverage',
  'drinks': 'beverage',
  'boisson': 'beverage',
  'boissons': 'beverage',
  'cocktail': 'beverage',
  'cocktails': 'beverage',
  'smoothies': 'beverage',
  'jus': 'beverage',
  'smoothies et cocktails': 'beverage',
  // sauce
  'sauce': 'sauce',
  'sauces': 'sauce',
  'condiment': 'sauce',
  'condiments': 'sauce',
  'vinaigrette': 'sauce',
  'vinaigrettes': 'sauce',
  'dressing': 'sauce',
  'dip': 'sauce',
  'dips': 'sauce',
  'trempette': 'sauce',
  'trempettes': 'sauce',
  'trempettes et houmous': 'sauce',
  'marinade': 'sauce',
  'marinades': 'sauce',
  'sauces et condiments': 'sauce',
  'confiture': 'sauce',
  'confitures': 'sauce',
  'jam': 'sauce',
  'vinaigrettes et mayonnaises': 'sauce',
  'mayonnaise': 'sauce',
  'mayonnaises': 'sauce',
  'tartinade': 'sauce',
  'tartinades': 'sauce',
};
// Deliberately NOT mapped: 'recettes', 'recipes' — every K pour Katrine tag
// carries it and it says nothing about the dish.

// Substring fallback for fragments the exact map doesn't know: source sites
// pad their categories ('Sweet treats', 'Dessert Recipes', 'Breakfast & Brunch')
// and the taxonomy cannot enumerate every variant. Only unambiguous English
// stems belong here — this runs after the exact lookup, never before it, so a
// precise French tag is always preferred.
const STEM_MAP: Array<[string, CanonicalDishType]> = [
  ['main course', 'main course'],
  ['main dish', 'main course'],
  ['side dish', 'side dish'],
  ['breakfast', 'breakfast'],
  ['brunch', 'breakfast'],
  ['dessert', 'dessert'],
  ['sweet', 'dessert'],
  ['snack', 'snack'],
  ['appetizer', 'appetizer'],
  ['appetiser', 'appetizer'],
  ['starter', 'appetizer'],
  ['beverage', 'beverage'],
  ['drink', 'beverage'],
  ['cocktail', 'beverage'],
  ['smoothie', 'beverage'],
  ['condiment', 'sauce'],
  ['sauce', 'sauce'],
  ['dip', 'sauce'],
  ['salad', 'salad'],
  ['soup', 'soup'],
];

// Title patterns checked in order — most distinctive categories first.
// Patterns run on the accent-folded title.
//
// `unless` is a guard checked before the match counts, replacing the negative
// lookbehind this table used to carry: lookbehind is not reliably available on
// Hermes, and this module is now imported by client code.
const TITLE_PATTERNS: Array<{
  re: RegExp;
  unless?: RegExp;
  dishType: CanonicalDishType;
  confidence: number;
}> = [
  { re: /\b(smoothie(?! bowl)|jus|juice|cocktail|mocktail|milkshake|slushie|latte|frappe|kombucha|boisson|lemonade|limonade|chai|sangria|punch)\b/, dishType: 'beverage', confidence: 85 },
  { re: /\b(soupe?|potage|chowder|bisque|minestrone|veloute|gaspacho|gazpacho|bouillon)\b/, dishType: 'soup', confidence: 85 },
  { re: /\b(salades?)\b/, dishType: 'salad', confidence: 80 },
  // snack before breakfast: "barres tendres granola" is a snack even though
  // "granola" alone reads breakfast.
  { re: /\b(energy balls?|barres? tendres?|granola bars?|trail mix|popcorn|muffins?)\b/, dishType: 'snack', confidence: 75 },
  { re: /\b(pancakes?|crepes?|waffles?|gaufres?|oatmeal|gruau|omelette|porridge|french toast|pain dore|smoothie bowl|granola)\b/, dishType: 'breakfast', confidence: 80 },
  { re: /\b(cheesecake|brownies?|cookies?|biscuits?|cupcakes?|tartelettes?|sorbet|tiramisu|clafoutis|fudge|macarons?|ice cream|creme brulee|mousse au chocolat|pouding|pudding|sucre a la creme)\b/, dishType: 'dessert', confidence: 80 },
  // "gateau"/"cake" is dessert unless it's a savory cake (crab cake, fish cake…)
  { re: /\b(gateaux?|cakes?)\b/, unless: /\b(crab|fish|potato|rice|salmon|corn|crispy) cakes?\b/, dishType: 'dessert', confidence: 75 },
  { re: /\b(tarte au[x]? (chocolat|sucre|citron|pommes?|fraises?|bleuets?|pacanes?))\b/, dishType: 'dessert', confidence: 80 },
  // Condiments only at the START of the title. "Sauce rosée" is a sauce;
  // "Poulet sauce rosée" is a main course, and "Pâtes au pesto" is dinner.
  // Matching these anywhere is what barred seven real dinners from souper
  // (#609) — the same lesson CONDIMENT_TITLE_START_RE in
  // recipeClassification.ts already encodes. Deliberately anchored rather
  // than lookbehind-guarded: Hermes has no reliable lookbehind.
  { re: /^(sauce|dressing|salsa|marinade|mayonnaise|aioli|ketchup|dip|vinaigrette|pesto|chutney|houmous|hummus|tzatziki|guacamole|compote|confiture)\b/, dishType: 'sauce', confidence: 80 },
];

/**
 * Raw dishTypes → canonical types.
 *
 * Handles the shapes real data arrives in: a clean single tag
 * (["Petit-déjeuner"]), a comma-joined blob of the source site's breadcrumb
 * (["Déjeuners et brunchs, Recettes"], ["breakfast, dinner, easy, lunch, main
 * course"]), and the nested case — schema.org permits recipeCategory to be a
 * string OR an array, and callers wrap whatever they were handed in an array,
 * so a publisher's array arrives here as [["Plats principaux"]].
 *
 * Non-strings are skipped rather than coerced. This used to throw
 * `value.split is not a function`, and because classifyDishTypeSync is called
 * outside the try block in classifyDishType, that surfaced as a 500 on the
 * import rather than a recipe that simply failed to classify.
 *
 * Fragments that aren't in the vocabulary are dropped rather than guessed at,
 * so an unrecognised tag behaves exactly like no tag at all.
 */
export function canonicalDishTypes(
  raw: readonly unknown[] | undefined
): CanonicalDishType[] {
  if (!raw?.length) return [];
  const out: CanonicalDishType[] = [];
  // Depth-1 flatten covers the nested array; anything else is not a category.
  const values = raw.flatMap(v => (Array.isArray(v) ? v : [v]));
  for (const value of values) {
    if (typeof value !== 'string') continue;
    for (const fragment of value.split(/[,/·|]/)) {
      const folded = fold(fragment);
      const mapped = CATEGORY_MAP[folded] ?? STEM_MAP.find(([stem]) => folded.includes(stem))?.[1];
      if (mapped && !out.includes(mapped)) out.push(mapped);
    }
  }
  return out;
}

/**
 * Recover the publisher's own category from the recipe URL.
 *
 * Several sites file recipes under a category path segment:
 *   /fr/recettes/plats-principaux/legumineuses/salade-de-pois-chiches...  -> main course
 *   /fr/recettes/dejeuners/bircher-muesli-aux-bleuets                     -> breakfast
 *
 * Deliberately strict, and the strictness is the whole point:
 *   - the segment must be an EXACT vocabulary key, never a stem match;
 *   - the LAST segment is skipped, because it is the recipe's own title slug.
 * Without both rules this reads the title instead of the category, and
 * `/recipes/1014524-quick-tomato-white-bean-and-kale-soup` becomes a soup while
 * `pasta-with-mussels-in-tomato-sauce` becomes a sauce — the recipe gets barred
 * from dinner on the strength of its own name.
 */
export function dishTypeFromSourceUrl(sourceUrl?: string): CanonicalDishType | null {
  if (!sourceUrl) return null;
  let segments: string[];
  try {
    segments = new URL(sourceUrl).pathname.split('/').filter(Boolean);
  } catch {
    return null;
  }
  for (const segment of segments.slice(0, -1)) {
    const mapped = CATEGORY_MAP[fold(segment.replace(/[-_]/g, ' '))];
    if (mapped) return mapped;
  }
  return null;
}

/**
 * Synchronous layers only: schema.org normalization then title heuristics.
 * Cheap enough to run on every import.
 */
export function classifyDishTypeSync(
  params: { title: string; rawCategory?: string; sourceUrl?: string }
): ClassificationResult | null {
  if (params.rawCategory) {
    const [mapped] = canonicalDishTypes([params.rawCategory]);
    if (mapped) {
      return { dishType: mapped, confidence: 95, source: 'normalized' };
    }
  }

  // The URL path is still the publisher's own classification, not our guess, so
  // it outranks the title heuristics below.
  const fromUrl = dishTypeFromSourceUrl(params.sourceUrl);
  if (fromUrl) {
    return { dishType: fromUrl, confidence: 90, source: 'url-path' };
  }

  const foldedTitle = fold(params.title || '');
  for (const { re, unless, dishType, confidence } of TITLE_PATTERNS) {
    if (re.test(foldedTitle) && !unless?.test(foldedTitle)) {
      return { dishType, confidence, source: 'heuristic' };
    }
  }

  return null;
}

export function isCanonicalDishType(value: unknown): value is CanonicalDishType {
  return typeof value === 'string' && (CANONICAL_DISH_TYPES as readonly string[]).includes(value);
}
